import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatErr } from "../context/AuthContext";
import { useTutorial } from "../components/TutorialProvider";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, User, GraduationCap, PlayCircle, Bell } from "lucide-react";
import NotificationsButton from "../components/NotificationsButton";

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const tutorial = useTutorial();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tutBusy, setTutBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setMarketing(!!user.marketing_opt_in);
    }
  }, [user, loading, navigate]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name, phone, marketing_opt_in: marketing });
      await refresh();
      toast.success("Profilo aggiornato");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  if (!user) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">...</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-2xl mx-auto p-6">
        <div className="mt-6 mb-6">
          <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Il tuo profilo</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900">Impostazioni</h1>
        </div>

        <Card className="p-6 mb-4">
          <div className="flex items-center gap-2 mb-4 text-blue-800"><User className="w-5 h-5"/><span className="text-xs uppercase tracking-widest font-semibold">Dati personali</span></div>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Email</label>
              <Input value={user.email} disabled/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Nome</label>
              <Input value={name} onChange={e=>setName(e.target.value)} data-testid="profile-name"/>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Telefono</label>
              <Input value={phone} onChange={e=>setPhone(e.target.value)} data-testid="profile-phone"/>
            </div>
          </div>
        </Card>

        <Card className="p-6 mb-4">
          <div className="flex items-center gap-2 mb-4 text-blue-800"><Mail className="w-5 h-5"/><span className="text-xs uppercase tracking-widest font-semibold">Comunicazioni</span></div>
          <label className="flex items-start justify-between gap-3 cursor-pointer">
            <div className="flex-1">
              <div className="font-medium text-slate-900">Email promozionali</div>
              <p className="text-sm text-slate-600 mt-1">Ricevi offerte, novità e promozioni Energy World. Puoi disattivarle in qualsiasi momento.</p>
            </div>
            <Switch checked={marketing} onCheckedChange={setMarketing} data-testid="profile-marketing"/>
          </label>
        </Card>

        <Card className="p-6 mb-4">
          <div className="flex items-center gap-2 mb-4 text-blue-800"><Bell className="w-5 h-5"/><span className="text-xs uppercase tracking-widest font-semibold">Notifiche push</span></div>
          <NotificationsButton variant="card" />
        </Card>

        <Card className="p-6 mb-4">
          <div className="flex items-center gap-2 mb-4 text-blue-800">
            <GraduationCap className="w-5 h-5"/>
            <span className="text-xs uppercase tracking-widest font-semibold">Tutorial guidato</span>
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="font-medium text-slate-900">
                {user.tutorial_completed ? "Rivedi il tour guidato" : "Scopri l'app in 1 minuto"}
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Un piccolo tour con suggerimenti e dati di esempio adatti al tuo ruolo ({user.role}).
                I dati di esempio vengono rimossi automaticamente al termine o se lo salti.
              </p>
            </div>
            <Button
              onClick={async () => {
                setTutBusy(true);
                try { await tutorial.start(); }
                catch (e) { toast.error(formatErr(e)); } finally { setTutBusy(false); }
              }}
              disabled={tutBusy || tutorial?.starting || tutorial?.active}
              className="bg-blue-800 hover:bg-blue-900 whitespace-nowrap"
              data-testid="profile-restart-tutorial">
              {tutBusy || tutorial?.starting ? <Loader2 className="w-4 h-4 animate-spin"/> :
                <><PlayCircle className="w-4 h-4 mr-1"/> {user.tutorial_completed ? "Rivedi tutorial" : "Avvia tutorial"}</>}
            </Button>
          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Link to="/change-password" className="text-sm text-blue-800 hover:underline flex items-center gap-1" data-testid="profile-change-pw">
            <KeyRound className="w-4 h-4"/> Cambia password
          </Link>
          <Button onClick={save} disabled={busy} className="bg-blue-800 hover:bg-blue-900" data-testid="profile-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salva modifiche"}
          </Button>
        </div>
      </div>
    </div>
  );
}
